import { useMemo } from 'react'
import { usePermission } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/mutations'
import { useShiftPatterns } from '@/features/shiftPatterns/queries'

/**
 * What `ShiftHandoverFormDialog` needs besides the handover itself — the shifts
 * on offer, whether they failed to load, and which link lists the reader may
 * add to. Shared by the list (which files) and the detail (which edits).
 *
 * ── The shifts a person may file against ───────────────────────────────
 * The session's own roster — the only shifts the server accepts from them.
 * Shift Master's catalogue only for a reader who may read it, root included,
 * who is exempt from the roster rule. See `ShiftHandoverFormDialog`.
 *
 * `shiftNames` is every shift name this client knows, for the list's Shift
 * column and filter — the list item carries only `shift_id`.
 */
export function useHandoverFormOptions({ enabled = true } = {}) {
  const session = useSession()
  const rostered = useMemo(
    () =>
      (session.data?.shifts ?? []).map((shift) => ({
        id: shift.id,
        name: shift.name ?? '',
        start: (shift.start_time ?? '').slice(0, 5),
        end: (shift.end_time ?? '').slice(0, 5),
      })),
    [session.data],
  )
  const canReadPatterns = usePermission('shifts.manage').granted
  const patterns = useShiftPatterns({ enabled: enabled && canReadPatterns })
  const shifts = useMemo(
    () => (canReadPatterns ? (patterns.data?.items ?? []) : rostered),
    [canReadPatterns, patterns.data, rostered],
  )
  const shiftNames = useMemo(() => {
    const names = new Map()
    for (const shift of rostered) names.set(shift.id, shift.name)
    for (const shift of patterns.data?.items ?? []) names.set(shift.id, shift.name)
    return names
  }, [rostered, patterns.data])

  /* The grant each link list needs — the same keys `_LINKS` checks server-side. */
  const woManage = usePermission('work_orders.manage').granted
  const woAssigned = usePermission('work_orders.update_assigned').granted
  const logManage = usePermission('logbook.manage').granted
  const logRecord = usePermission('logbook.record_entries').granted
  const anomalyManage = usePermission('anomalies.manage').granted
  const canLink = {
    workOrders: woManage || woAssigned,
    logbookEntries: logManage || logRecord,
    anomalies: anomalyManage,
  }

  return {
    shifts,
    shiftNames,
    shiftsFailed: canReadPatterns && patterns.isError,
    canLink,
  }
}
